// src/features/condos/CondominioPanel/CondominioPanel.jsx
import React, { useEffect, useState } from 'react';

import {
  criarCondo,
  atualizarCondo,
  getCondominioById,
} from '../../../API/condos/Condos';

const EMPTY = {
  name: '',
  app_url: '',
  server_url: '',

  // CONFIG
  alarmes: true,
  acionamentos: true,
  dispositivos: true,
};

export default function CondominioPanel({
  mode,           // 'create' | 'edit'
  condominio,     // objeto com pelo menos { id, name, app_url, server_url }
  onClose,
  onSaved,        // callback para recarregar lista
}) {
  const [values, setValues] = useState(EMPTY);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // accordions abertos/fechados
  const [sectionsOpen, setSectionsOpen] = useState({
    config: true,
  });

  const toggleSection = (key) => {
    setSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Carrega detalhes no modo edição
  useEffect(() => {
    let alive = true;

    async function loadDetails() {
      if (mode !== 'edit' || !condominio?.id) {
        setValues((prev) => ({
          ...EMPTY,
          name: condominio?.name || '',
          app_url: condominio?.app_url || '',
          server_url: condominio?.server_url || '',
        }));
        return;
      }

      setLoadingData(true);
      setErr('');

      try {
        const res = await getCondominioById(condominio.id);
        const raw =
          res?.data?.condominio ??
          res?.condominio ??
          null;

        const base = raw || condominio || {};
        const configNorm = normalizeConfig(raw?.config);

        if (!alive) return;

        setValues({
          ...EMPTY,
          name: base.name || '',
          app_url: base.app_url || '',
          server_url: base.server_url || '',

          // config
          alarmes: configNorm.alarmes,
          acionamentos: configNorm.acionamentos,
          dispositivos: configNorm.dispositivos,
        });
      } catch (e) {
        console.error('Falha ao carregar detalhes do condomínio', e);
        if (alive) {
          setErr('Não foi possível carregar os dados do condomínio.');
        }
      } finally {
        if (alive) setLoadingData(false);
      }
    }

    loadDetails();

    return () => { alive = false; };
  }, [mode, condominio]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setValues((prev) => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr('');

    const payload = buildPayload(values);

    try {
      if (mode === 'edit' && condominio) {
        const res = await atualizarCondo(condominio.id, payload);
        if (!res?.ok) {
          throw new Error(res?.error || `request_failed_${res?.status ?? ''}`);
        }
      } else {
        const res = await criarCondo(payload);
        if (!res?.ok) {
          throw new Error(res?.error || `request_failed_${res?.status ?? ''}`);
        }
      }

      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      console.error('Falha ao salvar condomínio', e);
      setErr('Não foi possível salvar o condomínio.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) {
    return <div className="condo-panel">Carregando dados do condomínio...</div>;
  }

  return (
    <form className="condo-panel" onSubmit={handleSubmit}>
      {err && <div className="condo-panel-error">{err}</div>}

      {/* DADOS BÁSICOS */}
      <div className="condo-panel-field">
        <label>Nome</label>
        <input
          name="name"
          value={values.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="condo-panel-field">
        <label>Domínio (URL do SAP)</label>
        <input
          name="app_url"
          type="url"
          value={values.app_url}
          onChange={handleChange}
          placeholder="https://dominiodosap.com.br"
          required
        />
      </div>

      <div className="condo-panel-field">
        <label>Servidor (opcional)</label>
        <input
          name="server_url"
          value={values.server_url}
          onChange={handleChange}
          placeholder="ex: condominio.cmd-automacao.com"
        />
      </div>

      {/* CONFIG */}
      <div className="condo-accordion">
        <button
          type="button"
          className="condo-accordion-header"
          onClick={() => toggleSection('config')}
        >
          <span className="condo-accordion-title">Configurações</span>
          <span className="condo-accordion-toggle">
            {sectionsOpen.config ? '–' : '+'}
          </span>
        </button>

        {sectionsOpen.config && (
          <div className="condo-accordion-body">
            <div className="condo-panel-field">
              <label>
                <input
                  type="checkbox"
                  name="alarmes"
                  checked={values.alarmes}
                  onChange={handleChange}
                  style={{ marginRight: 6 }}
                />
                Alarmes
              </label>
            </div>

            <div className="condo-panel-field">
              <label>
                <input
                  type="checkbox"
                  name="acionamentos"
                  checked={values.acionamentos}
                  onChange={handleChange}
                  style={{ marginRight: 6 }}
                />
                Acionamentos
              </label>
            </div>

            <div className="condo-panel-field">
              <label>
                <input
                  type="checkbox"
                  name="dispositivos"
                  checked={values.dispositivos}
                  onChange={handleChange}
                  style={{ marginRight: 6 }}
                />
                Dispositivos
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="condo-panel-footer">
        <button
          type="button"
          className="condo-btn secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="condo-btn primary"
          disabled={saving}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}

/* ===== Helpers ===== */

function normalizeConfig(config) {
  if (!config) {
    return {
      alarmes: true,
      acionamentos: true,
      dispositivos: true,
    };
  }

  return {
    alarmes: config.alarmes ?? true,
    acionamentos: config.acionamentos ?? true,
    dispositivos: config.dispositivos ?? true,
  };
}

function buildPayload(values) {
  return {
    name: values.name,
    app_url: values.app_url,
    server_url: values.server_url || null,

    config: {
      alarmes: !!values.alarmes,
      acionamentos: !!values.acionamentos,
      dispositivos: !!values.dispositivos,
      destinatarios: [],
    },
  };
}